import React, { useState } from 'react';
import { Col, FormGroup, Label, Row } from 'reactstrap';
import PropTypes from 'prop-types';
import SweetAlert from 'react-bootstrap-sweetalert';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  rectSortingStrategy,
  arrayMove,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import ReactDatetime from 'react-datetime';

import { TbDragDrop, TbTrash, TbEye, TbX, TbCirclePlusFilled } from 'react-icons/tb';
import moment from 'moment';
import VehicleImagesPopupSlider from '../../common/components/VehicleImagesPopupSlider';
import { sortPhotosByPerspective } from '../../helpers/sortPhotosByPerspective';
import { checkInteriorPerspective } from '../../helpers/checkInteriorPerspective';
import { PhotoUploadState } from '../../constants/PhotoUploadState';
import { MAX_PHOTOS_AMOUNT } from '../../constants/RequiredPhotosAmount';
import { PerspectiveType } from '../../constants/PerspectiveType';
import warningIcon from '../../assets/img/warningIcon.svg';
import { getPhotoDescription } from '../../helpers/getPhotoDescription';
import VehiclePhotoEditForm from './VehiclePhotoEditForm';
import { AuctionStatus } from '../../constants/AuctionStatus';
import {
  dateTimeToString,
  EXTENDED_DATE_TIME_FORMAT,
} from '../../common/utils/date/Date.utils';
import {
  FormCurrencyInputField,
  FormDatePickerField,
  FormSelectField,
  FormCheckboxField,
} from '../../common/components/formFields';
import { constantToSelect } from '../../common/helpers';
import { AuctionStatusType, AuctionStatusTypeName } from '../../constants/AuctionStatusType';
import { DueInDateType, DueInDateTypeName } from '../../constants/DueInDateType';

const modifiedAuctionTypes = AuctionStatusTypeName;
delete modifiedAuctionTypes.TRYING_TO_DEAL;
const auctionTypes = constantToSelect(modifiedAuctionTypes);
const dueInDateTypes = constantToSelect(DueInDateTypeName);

function VehicleMediaImageForm({ form, auction }) {
  const [showPopupSlider, setShowPopupSlider] = useState(false);
  const [photoEditIndex, setPhotoEditIndex] = useState(null);
  const [showDamagesPopupSlider, setShowDamagesPopupSlider] = useState(false);
  const [damages, setDamages] = useState([]);
  const [initialPhoto, setInitialPhoto] = useState(null);
  const [isInteriorPhoto, setIsInteriorPhoto] = useState(false);
  const [datePickerValue, setDatePickerValue] = useState(() =>
    auction.endsOn ? moment.utc(auction.endsOn).local() : null,
  );
  const isDraftAuction = auction.status === AuctionStatus.DRAFT;
  const isLiveAuction = auction.status === AuctionStatus.PUBLISHED;

  const handleOpenSlider = (index) => {
    setInitialPhoto(index);
    setShowPopupSlider(true);
  };

  const handleAddAdditionalPhoto = () => {
    // Check if we've reached the maximum number of photos
    if (form?.values?.photos?.length >= MAX_PHOTOS_AMOUNT) {
      return;
    }
    setPhotoEditIndex(-1);
  };

  const handleDelete = (mediaMetadataId) => {
    const newState = form?.values?.photos.filter(
      (item) => item.mediaMetadataId !== mediaMetadataId,
    );
    newState.forEach((item, index) => {
      item.perspective = getPhotoDescription(index + 1).PERSPECTIVE;
      item.sortNumber = index;
    });
    const photosToDelete = form?.values?.photosToDelete || [];
    photosToDelete.push(mediaMetadataId);
    form.setFieldValue('photos', newState);
    form.setFieldValue('photosToDelete', photosToDelete);
  };

  const handleUpdatePhoto = (photo, photosIdsToDelete) => {
    const { photos } = form.values;
    const photosToDelete = form.values.photosToDelete || [];
    if (photoEditIndex === -1) {
      photos.push(photo);
    } else {
      photos[photoEditIndex] = photo;
    }
    form.setFieldValue('photos', photos);
    form.setFieldValue('photosToDelete', [...photosToDelete, ...photosIdsToDelete]);
    setPhotoEditIndex(null);
  };

  const canEdit = (item) => {
    if (item.status === PhotoUploadState.IN_PROGRESS) return false;
    return item.status !== PhotoUploadState.PENDING;
  };

  const getPhotoUrl = (item) => {
    if (item?.urlTemp) {
      return item?.urlTemp;
    }
    return `${item?.fileUrl}`;
  };

  const renderAddPhotoButton = () => {
    if (form?.values?.photos?.length >= MAX_PHOTOS_AMOUNT) {
      return (
        <div className="vehicle_details__max-photos-message m-1">
          <p>Maximum of {MAX_PHOTOS_AMOUNT} photos reached</p>
        </div>
      );
    }

    return (
      <button
        type="button"
        className="vehicle_details__add-photo-button m-1"
        onClick={handleAddAdditionalPhoto}
      >
        <div className="vehicle_details__add-button_icon">
          <TbCirclePlusFilled color="#252153" size={50} />
        </div>
      </button>
    );
  };

  const renderInputDependsOnType = (type) => {
    if (type === AuctionStatusType.PHYSICAL)
      return (
        <FormCurrencyInputField
          showEmptyValue
          name="autoAcceptPriceInPence"
          label="Reserve Price"
          invalid={!!form.errors.autoAcceptPriceInPence}
          disabled={!(isDraftAuction || isLiveAuction)}
        />
      );
    if (type === AuctionStatusType.MINIMUM_PRICE)
      return (
        <FormCurrencyInputField
          name="minimumPriceInPence"
          label="Price"
          invalid={!!form.errors.minimumPriceInPence}
          disabled={!(isDraftAuction || isLiveAuction) || !auction.isMinimumPriceChangeAllowed}
        />
      );
    if (type === AuctionStatusType.TRYING_TO_DEAL)
      return (
        <>
          <FormSelectField
            name="tryingToDealDeliveryDateType"
            label="Due in date"
            placeholder="Due in Date"
            options={dueInDateTypes}
            disabled={!(isDraftAuction || isLiveAuction)}
          />
          {form?.values?.tryingToDealDeliveryDateType?.value === DueInDateType.CUSTOM_DATE && (
            <FormDatePickerField
              name="tryingToDealDeliveryDate"
              label="Date"
              disabled={!(isDraftAuction || isLiveAuction)}
            />
          )}
        </>
      );
    return null;
  };

  // eslint-disable-next-line react/no-unstable-nested-components
  function SortablePhotoTile({ id, children }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
      useSortable({ id });
    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      cursor: 'grab',
      opacity: isDragging ? 0.8 : 1,
    };
    return (
      <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
        {children}
      </div>
    );
  }

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor),
  );

  const sortedPhotos = sortPhotosByPerspective(form?.values?.photos);
  return (
    <>
      <Row>
        {isLiveAuction && (
          <Col md="4">
            <FormGroup>
              <Label>New Ends Time</Label>
              <ReactDatetime
                inputProps={{
                  onKeyDown: (e) => {
                    e.preventDefault();
                  },
                  className: 'form-control cursor-pointer',
                }}
                dateFormat="DD/MM/YYYY"
                timeFormat="hh:mm A"
                value={moment.utc(datePickerValue).local().format('DD/MM/YYYY hh:mm A')}
                onChange={(value) => {
                  if (moment.isMoment(value)) {
                    const formatted = value.utcOffset(120).format(EXTENDED_DATE_TIME_FORMAT);
                    setDatePickerValue(formatted);
                    form.setFieldValue('endsOn', formatted);
                  }
                }}
                isValidDate={(currentDate) => currentDate.isSameOrAfter(moment(), 'day')}
              />
              {form.errors.endsOn && (
                <label className="error error-label">{form.errors.endsOn}</label>
              )}
            </FormGroup>
          </Col>
        )}
        <Col md={isLiveAuction ? 4 : 6}>
          <FormSelectField
            name="type"
            label="Auction Type"
            options={auctionTypes}
            disabled={!isDraftAuction}
          />
        </Col>
        <Col md={isLiveAuction ? 4 : 6}>
          {renderInputDependsOnType(form?.values?.type?.value)}
        </Col>
        {auction.status === AuctionStatus.PUBLISHED && (
          <Col lg={12} className="mb-3">
            <p className="fw-bold">Current Ends Time: {dateTimeToString(auction.endsOn)}</p>
          </Col>
        )}
        <Col md="12" className="mb-3">
          <FormCheckboxField
            name="isBuyNowFeatureEnable"
            label="Buy Now Offer"
            onChange={(e) => {
              form.setFieldValue('isBuyNowFeatureEnable', e.target.checked);
            }}
            disabled
          />
        </Col>
        <Col md="12" className="mb-3">
          <FormCurrencyInputField
            name="buyNowPriceInPence"
            label="Buy Now Price"
            disabled={!form?.values?.isBuyNowFeatureEnable}
          />
        </Col>
      </Row>
      <h4>Add photo</h4>
      <Row className="media-upload">
        <Col>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(245px, 245px))',
              gap: 8,
              justifyContent: 'start',
            }}
          >
            {renderAddPhotoButton()}
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={({ active, over }) => {
                if (!over || active.id === over.id) return;
                const getId = (it, idx) =>
                  it?.mediaMetadataId || it?.id || it?.fileUrl || it?.urlTemp || `idx-${idx}`;
                const current = form?.values?.photos || [];
                const currentIds = current.map((it, idx) => getId(it, idx));
                const oldIndex = currentIds.indexOf(active.id);
                const newIndex = currentIds.indexOf(over.id);
                if (oldIndex === -1 || newIndex === -1) return;
                const next = arrayMove(current, oldIndex, newIndex);
                next.forEach((p, i) => {
                  p.perspective = getPhotoDescription(i + 1).PERSPECTIVE;
                  p.sortNumber = i + 1;
                });
                form.setFieldValue('photos', next);
              }}
            >
              {(() => {
                const getId = (it, idx) =>
                  it?.mediaMetadataId || it?.id || it?.fileUrl || it?.urlTemp || `idx-${idx}`;
                const entries = (sortedPhotos || []).map((it, idx) => ({ item: it, idx }));
                const realEntries = entries.filter(
                  ({ item }) => !!item?.fileUrl || !!item?.urlTemp,
                );
                const draggableIds = realEntries.map(({ item, idx }) => getId(item, idx));
                return (
                  <SortableContext items={draggableIds} strategy={rectSortingStrategy}>
                    {realEntries.map(({ item, idx }) => {
                      const id = getId(item, idx);
                      return (
                        <SortablePhotoTile key={id} id={id}>
                          <div className="vehicle_details__photo-wrapper">
                            <div>
                              <img
                                src={getPhotoUrl(item)}
                                alt="Vehicle"
                                className="vehicle_details__photo no-selectable"
                              />
                            </div>
                            {canEdit(item) && (
                              <>
                                <div className="vehicle_details__drag-position no-selectable">
                                  <TbDragDrop size={24} color="#252153" />
                                </div>
                                <div className="vehicle_details__photo-icon-button-container no-selectable">
                                  <button
                                    data-index={idx}
                                    type="button"
                                    className="vehicle_details__photo-icon-button"
                                    onClick={() => handleOpenSlider(idx)}
                                  >
                                    <TbEye color="#252153" size={24} />
                                  </button>
                                  <button
                                    data-index={idx}
                                    type="button"
                                    className="vehicle_details__photo-icon-button"
                                    onClick={() => handleDelete(item.mediaMetadataId)}
                                  >
                                    <TbTrash color="red" size={20} />
                                  </button>
                                </div>
                              </>
                            )}
                            {item.perspective !== PerspectiveType.MASTER &&
                              item?.damages?.length > 0 && (
                                <div className="vehicle_details__photo-damages-icon no-selectable">
                                  <img src={warningIcon} alt="Warning icon" />
                                </div>
                              )}
                          </div>
                        </SortablePhotoTile>
                      );
                    })}
                  </SortableContext>
                );
              })()}
            </DndContext>
          </div>
        </Col>
      </Row>
      <SweetAlert
        custom
        onConfirm={() => {}}
        title=""
        showConfirm={false}
        onCancel={() => setPhotoEditIndex(null)}
        show={photoEditIndex !== null}
        customClass="vehicle_details__edit_photo_popup"
      >
        <VehiclePhotoEditForm
          photoEditIndex={photoEditIndex}
          photos={form?.values?.photos || []}
          onSave={handleUpdatePhoto}
          onCloseAlert={() => setPhotoEditIndex(null)}
        />
      </SweetAlert>
      <SweetAlert
        custom
        show={showPopupSlider}
        onCancel={() => {
          setShowPopupSlider(false);
        }}
        onConfirm={() => {}}
        title=""
        customButtons={
          <button
            type="button"
            className="photo_gallery_close_button"
            onClick={() => setShowPopupSlider(false)}
          >
            <TbX size={24} />
          </button>
        }
        customClass="photo_gallery_popup"
      >
        <VehicleImagesPopupSlider
          photos={form?.values?.photos}
          initial={initialPhoto}
          onDamageClick={(damages, perspective) => {
            setIsInteriorPhoto(checkInteriorPerspective(perspective));
            setShowDamagesPopupSlider(true);
            setDamages(damages);
          }}
        />
      </SweetAlert>
      <SweetAlert
        custom
        show={showDamagesPopupSlider}
        onCancel={() => {
          setShowDamagesPopupSlider(false);
        }}
        onConfirm={() => {}}
        title=""
        customButtons={
          // eslint-disable-next-line jsx-a11y/control-has-associated-label
          <button
            type="button"
            className="photo_gallery_return_button"
            onClick={() => setShowDamagesPopupSlider(false)}
          >
            <i className="fas fa-chevron-left" />
          </button>
        }
        customClass="photo_gallery_popup"
      >
        <VehicleImagesPopupSlider
          photos={damages}
          initial={0}
          isDamageSlider
          showImageTitle={!isInteriorPhoto}
        />
      </SweetAlert>
    </>
  );
}

VehicleMediaImageForm.propTypes = {
  form: PropTypes.object.isRequired,
};

export default VehicleMediaImageForm;
